import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { action } from 'mobx';
import { getAbortController, isCanceledRequest } from '~/helpers';
import { localeStore } from '~/mobx/localesStore';
import client from '~/services/client';
import { TLocale } from '~/types/main';

interface IHeader {
  [key: string]: string | TLocale;
}

interface IRequest {
  params: any;
  headers: IHeader;
  signal: AbortSignal;
}

export abstract class BaseStore {
  private abortStack: {
    [key: string]: AbortController;
  } = {};

  @action protected abort = (abortId: string): void => {
    this.abortStack[abortId]?.abort();
    this.abortStack[abortId] = getAbortController();
  };

  @action protected isAborted = (error: any): boolean => {
    return isCanceledRequest(error);
  };

  @action protected getParams = (abortId: string, body: any = null): IRequest => {
    return {
      params: body ?? [],
      headers: { 'Accept-Language': localeStore.language },
      signal: this.abortStack[abortId].signal,
    };
  };

  /**
   * Download file from API
   *
   * @param {string} config - Axios request config.
   * @param {string} filename - File name for downloading.
   * @param {string} filetype - File type for Blob.
   */
  @action downloadFile = async (url: string, config: AxiosRequestConfig) => {
    const response: AxiosResponse = await client.get(
      url,
      {
        ...config,
        responseType: 'blob',
        timeout: 5 * 60 * 1000, // For long requests
      },
      true
    );

    const filename = response.headers['content-disposition'].split(';')[1].split('=')[1].replace(/"/g, '').trim();
    const type = response.headers['content-type'];

    const blob = new Blob([response.data], { type });
    const link = document.createElement('a');
    const objectURL = window.URL.createObjectURL(blob);

    link.href = objectURL;
    link.download = filename;
    link.style.display = 'none';
    link.innerText = 'download';

    document.body.appendChild(link);
    link.click();

    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(objectURL);
  };
}
